// Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §3.3).
// Internal IDs only in addresses; details, names, responses and reasons travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  accessLogEntrySchema,
  accessLogPageSchema,
  recordReleasePageSchema,
  recordsRequestRowSchema,
  recordsRequestSchema,
  releaseResultSchema,
  type CloseRecordsRequestBody,
  type ReviewCorrectionBody,
  type CreateRecordsRequestBody,
  type CreateReleaseBody,
  type VerifyIdentityBody,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export const recordsRequestsQuery = (state: 'open' | 'closed') =>
  queryOptions({
    queryKey: ['records', 'requests', state],
    queryFn: () =>
      api('GET', `/records-requests?state=${state}&limit=100`, {
        schema: z.object({
          items: z.array(recordsRequestRowSchema),
          nextCursor: z.uuid().nullable(),
        }),
      }),
  });

export const recordsRequestQuery = (requestId: string) =>
  queryOptions({
    queryKey: ['records', 'request', requestId],
    queryFn: () => api('GET', `/records-requests/${requestId}`, { schema: recordsRequestSchema }),
  });

export const recordReleasesQuery = queryOptions({
  queryKey: ['records', 'releases'],
  queryFn: () => api('GET', '/record-releases?limit=100', { schema: recordReleasePageSchema }),
});

export const changeCandidatesQuery = (requestId: string) =>
  queryOptions({
    queryKey: ['records', 'request', requestId, 'candidates'],
    queryFn: () =>
      api('GET', `/records-requests/${requestId}/change-candidates`, {
        schema: z.object({ items: z.array(accessLogEntrySchema) }),
      }),
  });

export const accessLogPage = (patientId: string, cursor: string | null) =>
  api(
    'GET',
    `/patients/${patientId}/access-log?limit=50${cursor === null ? '' : `&cursor=${cursor}`}`,
    { schema: accessLogPageSchema },
  );

export const logRecordsRequest = (patientId: string, body: CreateRecordsRequestBody) =>
  api('POST', `/patients/${patientId}/records-requests`, { body, schema: recordsRequestSchema });

export const verifyIdentity = (requestId: string, body: VerifyIdentityBody) =>
  api('POST', `/records-requests/${requestId}/identity`, { body, schema: recordsRequestSchema });

export const closeRecordsRequest = (requestId: string, body: CloseRecordsRequestBody) =>
  api('POST', `/records-requests/${requestId}/close`, { body, schema: recordsRequestSchema });

/** Accept or decline a correction (docs/plans/golive-privacy-tooling.md §5.2). */
export const reviewCorrection = (requestId: string, body: ReviewCorrectionBody) =>
  api('POST', `/records-requests/${requestId}/review`, { body, schema: recordsRequestSchema });

export const linkChange = (requestId: string, auditEventId: string) =>
  api('POST', `/records-requests/${requestId}/changes`, {
    body: { auditEventId },
    schema: recordsRequestSchema,
  });

export const releaseRecord = (requestId: string, body: CreateReleaseBody) =>
  api('POST', `/records-requests/${requestId}/releases`, { body, schema: releaseResultSchema });
